import { Stack, Typography, Box, List, ListItem, Button } from "@mui/material";
import ModalComponent from "../Modal";
import { Info } from "@mui/icons-material";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { IMAGES } from "@/lib/constants";
import ButtonComponent from "../Button";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
import Link from "next/link";

const Item = ({ children, item = true }) => (
  <ListItem sx={{ display: item && "list-item", listStyleType: "disc" }}>
    <Typography>{children}</Typography>
  </ListItem>
);

export const PanAadharLinkingModal = ({
  open,
  handleModal,
  attemptsLeft,
  btnOnClick,
  dayLimit,
  panAadhaarHaltedAt,
  showBtn = true,
}) => {
  const t = useTranslations("offers.panAdharLinking");
  const router = useRouter();
  const [moreMethods, setMoreMethods] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Dynamic countdown timer
  useEffect(() => {
    if (!panAadhaarHaltedAt) {
      setCountdown(null);
      return;
    }
    // Convert UTC to IST before using for countdown
    const haltedDateIST = dayjs.utc(panAadhaarHaltedAt).tz("Asia/Kolkata");
    const updateCountdown = () => {
      const now = dayjs().tz("Asia/Kolkata");
      const diffHours = now.diff(haltedDateIST, "hour");
      if (diffHours < 24) {
        const remaining = 24 - now.diff(haltedDateIST, "hour", true);
        const hours = Math.floor(remaining);
        const minutes = Math.floor((remaining - hours) * 60);
        const seconds = Math.floor(((remaining - hours) * 60 - minutes) * 60);
        setCountdown(
          `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
        );
      } else {
        setCountdown(null);
      }
    };
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [panAadhaarHaltedAt]);

  return (
    <ModalComponent
      closeButtonRequired={false}
      title={
        <Typography fontSize={{ xs: 20, sm: 24 }} gutterBottom>
          {t.rich("kycVerification")}
        </Typography>
      }
      maxWidth="md"
      open={open}
      extraFunction={() => {}}
      handleModal={handleModal}
    >
      <Stack alignItems={"center"} gap={2}>
        <Image src={IMAGES.panAadharLinking} alt="Pan Aadhar linking error" />
        <Box
          bgcolor={"#fef7db"}
          p={2}
          borderRadius={4}
          border={1}
          borderColor={"#d2e3d9"}
        >
          <Stack direction="row" mb={1} alignItems="center" spacing={1}>
            <Info fontSize="small" style={{ color: "#ec9c06" }} />
            <Typography style={{ color: "#ec9c06" }}>
              {t.rich("linkPanWithAadhaar", {
                bold: (chunk) => <strong>{chunk}</strong>,
              })}
            </Typography>
          </Stack>

          <Typography>
            Your PAN is not linked to Aadhaar, which is mandatory for identity
            verification. Please complete the linking process within{" "}
            <strong>{dayLimit}</strong> days to continue your loan application.
          </Typography>
        </Box>

        <Typography
          fontWeight="bold"
          sx={{ alignSelf: "flex-start" }}
          pl={1}
          gutterBottom
        >
          {t.rich("linkingMethods", {
            bold: (chunk) => <strong>{chunk}</strong>,
          })}
        </Typography>
        <Stack
          bgcolor={"#f7fff6"}
          p={2.5}
          borderRadius={4}
          alignItems={"center"}
          borderColor={"#d5e4db"}
          border={1}
        >
          <Stack direction="row" mb={1} alignItems="center" spacing={1}>
            <Image src={IMAGES.globe} alt="Pan Aadhar linking error" />
            <Typography variant="subtitle1" gutterBottom>
              {t.rich("onlineMethod", {
                bold: (chunk) => <strong>{chunk}</strong>,
              })}
            </Typography>
          </Stack>
          <List
            component="ol"
            sx={{ listStyleType: "disc" }}
            dense
            disablePadding
            color={"#bfcdc7"}
          >
            <ListItem sx={{ display: "list-item", px: 0 }}>
              Visit{" "}
              <Link href="https://www.incometax.gov.in" target="_blank">
                {t.rich("visitWebsite", {
                  bold: (chunk) => <strong>{chunk}</strong>,
                })}
              </Link>
            </ListItem>
            <ListItem sx={{ display: "list-item", px: 0 }}>
              {t.rich("fillDetails", {
                bold: (chunk) => <strong>{chunk}</strong>,
              })}
            </ListItem>
            <ListItem sx={{ display: "list-item", px: 0 }}>
              {t.rich("submitVerify", {
                bold: (chunk) => <strong>{chunk}</strong>,
              })}
            </ListItem>
          </List>
        </Stack>
        <Stack justifyContent={"flex-end"} direction={"row"}>
          {(() => {
            if (!panAadhaarHaltedAt) return null;
            // Convert UTC to IST for attempt logic as well
            const haltedDateIST = dayjs
              .utc(panAadhaarHaltedAt)
              .tz("Asia/Kolkata");
            const now = dayjs().tz("Asia/Kolkata");
            return now.diff(haltedDateIST, "day") >= 1 ? (
              <Typography align="right" color={"red"} variant="subtitle1">
                No. of attempts remaining: {attemptsLeft}
              </Typography>
            ) : null;
          })()}
        </Stack>
        {moreMethods && (
          <>
            <Box
              bgcolor={""}
              p={2}
              borderRadius={4}
              border={1}
              borderColor={"#e6f1f1"}
            >
              {/* SMS Method */}
              <Stack direction="row" mb={1} alignItems="center" spacing={1}>
                <Image src={IMAGES.message} alt="Pan Aadhar linking error" />

                <Typography variant="subtitle1" gutterBottom>
                  {t.rich("smsMethod", {
                    bold: (chunk) => <strong>{chunk}</strong>,
                  })}
                </Typography>
              </Stack>

              <List dense disablePadding color={"#bfcdc7"}>
                <Box marginLeft={3}>
                  <Item>
                    {t.rich("sendSms", {
                      bold: (chunk) => <strong>{chunk}</strong>,
                    })}
                  </Item>
                  <Item item={false}>
                    {t.rich("smsFormat", {
                      bold: (chunk) => (
                        <strong
                          style={{
                            background: "#f7fff6",
                            padding: "2px 5px",
                            borderRadius: "4px",
                          }}
                        >
                          {chunk}
                        </strong>
                      ),
                    })}
                  </Item>
                  <Item item={false}>
                    {t.rich("sendTo", {
                      bold: (chunk) => <strong>{chunk}</strong>,
                    })}
                  </Item>
                  <Item item={false}>
                    {t.rich("smsExample", {
                      bold: (chunk) => <strong>{chunk}</strong>,
                    })}
                  </Item>
                </Box>
              </List>
            </Box>

            <Box
              bgcolor={""}
              p={1}
              borderRadius={4}
              border={1}
              borderColor={"#e6f1f1"}
            >
              <Stack direction="row" mb={1} alignItems="center" spacing={1}>
                <Image src={IMAGES.contactid} alt="Pan Aadhar linking error" />
                <Typography variant="subtitle1" gutterBottom>
                  {t.rich("offlineMethod", {
                    bold: (chunk) => <strong>{chunk}</strong>,
                  })}
                </Typography>
              </Stack>
              <List dense>
                <Box marginLeft={3}>
                  <Item>
                    {t.rich("visitCenter", {
                      bold: (chunk) => <strong>{chunk}</strong>,
                    })}
                  </Item>
                  <Item>
                    {t.rich("fillForm", {
                      bold: (chunk) => <strong>{chunk}</strong>,
                    })}
                  </Item>
                  <Item>
                    {t.rich("attachCopy", {
                      bold: (chunk) => <strong>{chunk}</strong>,
                    })}
                  </Item>
                  <Item>
                    {t.rich("payFee", {
                      bold: (chunk) => <strong>{chunk}</strong>,
                    })}
                  </Item>
                </Box>
              </List>
            </Box>
          </>
        )}

        {/* <Stack direction="row" spacing={2} mt={3}> */}
        <Button onClick={() => setMoreMethods((i) => !i)}>
          <Typography
            variant="body2"
            sx={{
              textDecoration: "underline",
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {t.rich(moreMethods ? "viewLessMethods" : "viewMoreMethods")}
          </Typography>
        </Button>
        {countdown && (
          <Typography color="red" variant="subtitle1">
            You can check linking status in: {countdown}
          </Typography>
        )}
        {showBtn && (
          <Stack>
            <ButtonComponent onClick={btnOnClick} type="submit">
              {t.rich("checkStatus")}
            </ButtonComponent>
          </Stack>
        )}
        <Stack>
          <ButtonComponent onClick={() => router.push("/")}>
            Go to HomePage
          </ButtonComponent>
        </Stack>
        {/* </Stack> */}
      </Stack>
    </ModalComponent>
  );
};
